import { Button } from '@ibsys/kit';
import { ChevronLeftIcon } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/shared/lib';
import { PageContainer, useMainLayout } from '@/shared/ui';
import { CompetencyTree } from './CompetencyTree/CompetencyTree';
import { CompetencyCard } from './CompetencyCard/CompetencyCard';

/** Ширина контейнера, при которой включается двухколоночный layout */
const DESKTOP_CONTAINER_WIDTH = 1280;

export const CoachingPage: FC = () => {
  const { competencyId } = useParams<{ competencyId?: string }>();
  const { t } = useTranslation(['common', 'coaching']);
  const navigate = useNavigate();

  const {
    breadcrumbs: { addBreadcrumbs },
  } = useMainLayout();

  useEffect(() => {
    addBreadcrumbs({
      path: '/competencies',
      label: t('coaching:title'),
    });
  }, [addBreadcrumbs, t]);

  /* Отслеживаем ширину контейнера страницы, а не viewport —
     чтобы корректно реагировать на сворачивание бокового меню */
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_CONTAINER_WIDTH);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      setIsDesktop(entry.contentRect.width >= DESKTOP_CONTAINER_WIDTH);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSelectCompetency = (id: number) => {
    navigate(`/competencies/${id}`);
  };

  const handleCompetencyDeleted = () => {
    navigate('/competencies');
  };

  const handleBack = () => {
    navigate('/competencies');
  };

  return (
    <div ref={containerRef} className="@container/coaching h-full">
      <PageContainer
        enableScroll={false}
        className="flex flex-col h-full @xl/coaching:grid @xl/coaching:grid-cols-[minmax(300px,400px)_1fr] grid-rows-1 @xl/coaching:gap-5"
      >
        {/* Дерево компетенций — скрывается при выбранном элементе, если не десктоп */}
        <div className={cn('h-full', competencyId ? 'hidden @xl/coaching:block' : 'block')}>
          <CompetencyTree
            selectedCompetencyId={competencyId ? +competencyId : undefined}
            onSelectCompetency={handleSelectCompetency}
            isDesktop={isDesktop}
          />
        </div>

        {/* Карточка компетенции — скрывается при отсутствии выбранного элемента, если не десктоп */}
        <div className={cn('flex flex-col h-full', !competencyId ? 'hidden @xl/coaching:flex' : 'flex')}>
          {/* Кнопка "К списку" — только когда не десктоп и есть выбранный элемент */}
          {competencyId && !isDesktop && (
            <Button
              variant="ghost"
              size="sm"
              className="mb-3 self-start -ml-2"
              icon={<ChevronLeftIcon className="size-4" />}
              onClick={handleBack}
            >
              {t('common:backToList')}
            </Button>
          )}

          <div className="h-full overflow-auto">
            {competencyId && <CompetencyCard competencyId={+competencyId} onDeleted={handleCompetencyDeleted} />}

            {!competencyId && (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {t('coaching:selectItem')}
              </div>
            )}
          </div>
        </div>
      </PageContainer>
    </div>
  );
};
